// BiometricAppLock — the "open the app → fingerprint → you're in" screen.
// Shown on mobile when the user enabled the lock and a signed-in session is
// being resumed. Real authentication (password + TOTP) already happened; this
// keeps the persisted session private on the device.
import React, { useState, useEffect, useCallback } from "react";
import { useFileContext } from "../../ContextAPI/FileContext";
import { isAppLockEnabled, verifyAppLock } from "../../utils/appLock";
import { isMobileDevice } from "../../utils/device";
import FingerprintIcon from "./FingerprintIcon";
import "./BiometricAppLock.css";

// Locked once per page load (a fresh app open), not on every re-render.
let unlockedThisLoad = false;
// Wall-clock of the last time the app went to the background, so returning after
// a while re-locks even when the OS kept the page alive in memory.
let hiddenSince = null;
const BACKGROUND_RELOCK_MS = 5 * 60 * 1000; // 5 minutes away -> ask again

const BiometricAppLock = () => {
  const { auth, logout } = useFileContext();
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!auth) {
      setLocked(false);
      return;
    }
    if (unlockedThisLoad) return;
    if (!isMobileDevice() || !isAppLockEnabled()) return;
    setLocked(true);
  }, [auth]);

  // Re-lock after time in the background. Without this the lock only fires on a
  // cold start, so returning to a still-in-memory tab would skip the biometric.
  useEffect(() => {
    if (!auth || !isMobileDevice() || !isAppLockEnabled()) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenSince = Date.now();
        return;
      }
      if (hiddenSince && Date.now() - hiddenSince >= BACKGROUND_RELOCK_MS) {
        unlockedThisLoad = false;
        setError("");
        setLocked(true);
      }
      hiddenSince = null;
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [auth]);

  const unlock = useCallback(async () => {
    setError("");
    setBusy(true);
    try {
      await verifyAppLock();
      unlockedThisLoad = true;
      setLocked(false);
    } catch (err) {
      const name = err?.name || "Error";
      if (
        name === "NotAllowedError" ||
        /cancel|timed out|not allowed/i.test(err?.message || "")
      ) {
        setError("Not recognized. Try again.");
      } else {
        setError(`Could not verify. [${name}: ${err?.message || "unknown"}]`);
      }
    } finally {
      setBusy(false);
    }
  }, []);

  // Offer the prompt immediately on open — one tap if the browser blocks the
  // ungestured call.
  useEffect(() => {
    if (locked && !busy && !error) {
      const t = setTimeout(() => {
        unlock();
      }, 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [locked]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!locked) return null;

  return (
    <div className="app-lock">
      <div className="app-lock__card">
        <div className="app-lock__icon" aria-hidden="true">
          <FingerprintIcon />
        </div>
        <h2 className="app-lock__title">Unlock RDV Systems</h2>
        <p className="app-lock__subtitle">
          Use your fingerprint or face to continue.
        </p>

        {error && <p className="app-lock__error">{error}</p>}

        <button
          type="button"
          className="app-lock__unlock"
          onClick={unlock}
          disabled={busy}
        >
          {busy ? "Waiting…" : "Unlock Using Biometrics"}
        </button>
        <button type="button" className="app-lock__signout" onClick={logout}>
          Sign out instead
        </button>
      </div>
    </div>
  );
};

export default BiometricAppLock;
